import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  Divider,
  Link,
} from "@mui/material";

const EFFECTIVE_DATE = "October 23, 2026";
const COMPANY_NAME = "A3 Assurance Inc.";
const PRODUCT_NAME = "Shop Floor Mgmt (SFM, Beta)";
const CONTACT_EMAIL = "legal@a3assurance.com";
const SITE_URL = "https://shopfloormgmt.com";

const Section = ({ title, children }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h6" fontWeight={700} gutterBottom>
      {title}
    </Typography>
    {children}
  </Box>
);

const P = ({ children }) => (
  <Typography variant="body1" sx={{ mb: 1.5, color: "text.secondary", lineHeight: 1.8 }}>
    {children}
  </Typography>
);

export default function TermsOfServicePage() {
  const navigate = useNavigate();

  return (
    <Box sx={{ backgroundColor: "#f9fafb", minHeight: "100vh", py: 6 }}>
      {/* Nav bar */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "#fff",
          borderBottom: "1px solid #e5e7eb",
          px: 4,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          variant="h6"
          fontWeight={800}
          sx={{ color: "#1e40af", cursor: "pointer" }}
          onClick={() => navigate("/")}
        >
          SFM
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button variant="outlined" size="small" onClick={() => navigate("/login")}>
            Log In
          </Button>
          <Button variant="contained" size="small" onClick={() => navigate("/signup")}>
            Start Free Trial
          </Button>
        </Box>
      </Box>

      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Box sx={{ backgroundColor: "#fff", borderRadius: 2, p: { xs: 3, md: 6 }, boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
          <Typography variant="h4" fontWeight={800} gutterBottom>
            Terms of Service
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Effective Date: {EFFECTIVE_DATE}
          </Typography>
          <Divider sx={{ mb: 4 }} />

          <P>
            These Terms of Service ("Terms") govern your access to and use of {PRODUCT_NAME},
            a software-as-a-service platform operated by {COMPANY_NAME} ("A3 Assurance," "we,"
            "us," or "our"). By registering for or using {PRODUCT_NAME}, you ("Customer" or "you")
            agree to be bound by these Terms.
          </P>

          <Section title="1. Definitions">
            <P>"Service" means the {PRODUCT_NAME} web application and any related APIs, documentation, and support provided at {SITE_URL}.</P>
            <P>"Subscription" means your paid, trial or no-charge access to one or more SFM Modules.</P>
            <P>"Module" means a discrete functional package within SFM (WeldAssure, SFM Inspection Plus, SFM Operations).</P>
            <P>"Authorized Users" means employees or contractors of Customer who are granted access credentials under Customer's account.</P>
            <P>"Customer Data" means all data uploaded, submitted, or generated within the Service by Customer or its Authorized Users.</P>
          </Section>

          <Section title="2. Access and License">
            <P>
              Subject to these Terms and payment of applicable fees, A3 Assurance grants you a
              limited, non-exclusive, non-transferable, revocable license to access and use the
              Service solely for your internal business operations during the Subscription term.
            </P>
            <P>
              You may not sublicense, resell, or otherwise make the Service available to third
              parties. You are responsible for all activity that occurs under your account credentials.
            </P>
          </Section>

          <Section title="3. Free Trial">
            <P>
              New accounts receive a 14-day free trial. During the trial, full module functionality
              is available. No credit card is required to start a trial. At the end of the trial
              period, continued access to paid Modules requires a paid Subscription. The core platform
              and the SFM Operations Module remain available at no charge. Unused trial days do not carry
              over to a paid Subscription.
            </P>
          </Section>

          <Section title="4. Subscriptions and Fees">
            <P>
              The core platform and the SFM Operations Module are provided at no charge. Each paid
              Module (SFM Inspection Plus and WeldAssure) is priced at $25.00 USD per month or
              $244.99 USD per year, as chosen at checkout, after any free trial. Fees are billed in
              advance for the selected billing period. All fees are non-refundable except as
              expressly stated in these Terms or required by applicable law.
            </P>
            <P>
              Subscriptions purchased directly from A3 Assurance renew automatically at the end of each
              billing period unless cancelled before the renewal date. You may cancel at any time;
              cancellation takes effect at the end of the current billing period. A3 Assurance
              reserves the right to change pricing with 60 days' written notice.
            </P>
            <P>
              Subscriptions purchased through the Apple App Store are sold, billed and renewed by
              Apple at the price shown in the App Store at the time of purchase, which may vary by
              country or region. They renew automatically unless cancelled at least 24 hours before
              the end of the current period, and are managed and cancelled in your Apple Account
              settings. Refunds for App Store purchases are handled by Apple under its policies. For App Store
              purchases, this paragraph governs where it differs from these Terms.
            </P>
          </Section>

          <Section title="5. Acceptable Use">
            <P>You agree not to: (a) use the Service to violate any applicable law or regulation; (b) upload malicious code, malware, or any content that infringes third-party intellectual property rights; (c) attempt to reverse engineer, decompile, or derive source code from the Service; (d) use automated means to access the Service in a manner that exceeds normal human usage patterns; (e) share login credentials across multiple organizations.</P>
          </Section>

          <Section title="6. Customer Data and Privacy">
            <P>
              You retain all ownership rights in your Customer Data. By uploading data to the
              Service, you grant A3 Assurance a limited license to process that data solely to
              provide and improve the Service. A3 Assurance will not sell your Customer Data to
              third parties.
            </P>
            <P>
              A3 Assurance implements commercially reasonable technical and organizational measures
              to protect Customer Data. Please review our{" "}
              <Link
                component="button"
                onClick={() => navigate("/privacy")}
                sx={{ fontWeight: 600 }}
              >
                Privacy Policy
              </Link>{" "}
              for full details.
            </P>
          </Section>

          <Section title="7. Intellectual Property">
            <P>
              A3 Assurance retains all right, title, and interest in the Service, including all
              software, interfaces, documentation, and underlying technology. These Terms do not
              grant you any intellectual property rights in the Service beyond the limited license
              described in Section 2.
            </P>
          </Section>

          <Section title="8. Confidentiality">
            <P>
              Each party agrees to keep confidential any non-public information disclosed by the
              other party that is designated as confidential or that reasonably should be understood
              to be confidential. This obligation survives termination of these Terms for three (3)
              years.
            </P>
          </Section>

          <Section title="9. Warranties and Disclaimers">
            <P>
              A3 Assurance warrants that the Service will perform materially in accordance with its
              documentation during the Subscription term. EXCEPT FOR THIS LIMITED WARRANTY, THE
              SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTY OF ANY KIND. A3 ASSURANCE DISCLAIMS ALL
              IMPLIED WARRANTIES, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
              PURPOSE, AND NON-INFRINGEMENT.
            </P>
            <P>
              Hardware not included. The Service is software only. A3 Assurance does not sell,
              supply, install, maintain or support hardware, networks or third-party equipment,
              including Apple TV devices, displays, tablets, phones, label printers, scanners,
              mounting and internet or Wi-Fi service. You are responsible for selecting, purchasing,
              installing, securing and maintaining that equipment and for its compatibility with
              the Service. A3 Assurance makes no warranty for that equipment and is not responsible
              for any interruption, delay or loss of data caused by it.
            </P>
          </Section>

          <Section title="10. Limitation of Liability">
            <P>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, NEITHER PARTY WILL BE LIABLE FOR INDIRECT,
              INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES. A3 ASSURANCE'S TOTAL
              CUMULATIVE LIABILITY ARISING OUT OF OR RELATED TO THESE TERMS WILL NOT EXCEED THE
              FEES PAID BY YOU IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
            </P>
          </Section>

          <Section title="11. Indemnification">
            <P>
              You agree to indemnify and hold harmless A3 Assurance and its officers, directors,
              and employees from any claims, damages, or expenses (including reasonable legal fees)
              arising from: (a) your use of the Service in violation of these Terms; (b) your
              Customer Data; or (c) your violation of any applicable law.
            </P>
          </Section>

          <Section title="12. Suspension and Termination">
            <P>
              A3 Assurance may suspend or terminate your access immediately upon notice if: (a) you
              violate these Terms; (b) your payment fails and is not cured within 7 days; or (c)
              required by law. You may cancel your Subscription at any time through the billing
              portal (or, for App Store purchases, in your Apple Account settings), effective at the end of the current billing period. Upon termination, you
              may export your Customer Data within 30 days.
            </P>
          </Section>

          <Section title="13. Governing Law and Disputes">
            <P>
              These Terms are governed by the laws of the Province of Ontario, Canada, without
              regard to conflict-of-law principles. Any dispute shall be resolved through binding
              arbitration in accordance with the rules of the Canadian Arbitration Association,
              except either party may seek injunctive relief in any court of competent jurisdiction.
            </P>
          </Section>

          <Section title="14. Changes to These Terms">
            <P>
              A3 Assurance may update these Terms from time to time. We will notify you by email
              or in-app notice at least 30 days before material changes take effect. Continued use
              of the Service after the effective date constitutes acceptance of the updated Terms.
            </P>
          </Section>

          <Section title="15. Contact">
            <P>
              Questions about these Terms? Contact us at{" "}
              <Link href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Link>.
            </P>
          </Section>

          <Divider sx={{ my: 4 }} />
          <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
            <Button variant="outlined" onClick={() => navigate("/privacy")}>
              Privacy Policy
            </Button>
            <Button variant="contained" onClick={() => navigate("/signup")}>
              Start Free Trial
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
